import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';
import { getSupabaseKey } from '@/lib/env';
import {
  SUPPORT_ATTACHMENT_BUCKET,
  createSupportAttachmentSignedUrl,
  type SupportAttachmentUpload,
} from '@/lib/supportAttachments';

export const SUPPORT_MESSAGE_MAX_LENGTH = 2000;
const SUPPORT_FUNCTIONS_BASE_URL = 'https://service.canxp.ai/functions/v1';

export type SupportConversation = Tables<'support_conversations'>;
export type SupportMessage = Tables<'support_messages'>;
export type SupportProfileSummary = Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email' | 'avatar_url'>;

export type SupportMessageLike = SupportMessage & {
  optimistic?: boolean;
};

export type SupportConversationThread = SupportConversation & {
  profile: SupportProfileSummary | null;
};

export interface SendSupportMessageResult {
  ok: boolean;
  conversation: SupportConversation;
  message: SupportMessage;
  error?: string;
}

interface CreateOptimisticSupportMessageOptions {
  senderType?: SupportMessage['sender_type'];
  direction?: SupportMessage['direction'];
  deliveryStatus?: SupportMessage['delivery_status'];
  mediaUrl?: string | null;
  mediaType?: string | null;
  metadata?: SupportMessage['metadata'];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getMessageMetadata(message: Pick<SupportMessageLike, 'metadata'>) {
  return isRecord(message.metadata) ? message.metadata : {};
}

function getAttachmentPath(message: Pick<SupportMessageLike, 'media_url' | 'metadata'>) {
  const metadata = getMessageMetadata(message);
  const pathFromMetadata = typeof metadata.attachment_path === 'string' ? metadata.attachment_path : null;
  const rawMediaUrl = typeof message.media_url === 'string' ? message.media_url : null;

  if (pathFromMetadata) {
    return pathFromMetadata;
  }

  if (rawMediaUrl && !/^https?:\/\//i.test(rawMediaUrl) && !rawMediaUrl.startsWith('blob:')) {
    return rawMediaUrl;
  }

  return null;
}

function getAttachmentBucket(message: Pick<SupportMessageLike, 'metadata'>) {
  const metadata = getMessageMetadata(message);
  return typeof metadata.attachment_bucket === 'string' ? metadata.attachment_bucket : null;
}

async function requireSessionUser() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token || !session.user) {
    throw new Error('You must be logged in to contact support.');
  }

  return session;
}

export async function fetchLatestSupportConversation(userId: string) {
  const { data, error } = await supabase
    .from('support_conversations')
    .select('*')
    .eq('user_id', userId)
    .eq('channel_type', 'whatsapp')
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}

export async function fetchSupportConversationByRef(reference: string) {
  const normalizedReference = reference.trim().toUpperCase();
  const { data, error } = await supabase
    .from('support_conversations')
    .select('*')
    .eq('site_conversation_ref', normalizedReference)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}

export async function fetchAdminSupportConversations() {
  const { data, error: conversationsError } = await supabase
    .from('support_conversations')
    .select('*')
    .order('last_message_at', { ascending: false });

  if (conversationsError) {
    throw conversationsError;
  }

  const conversations = (data || []) as SupportConversation[];
  const userIds = [...new Set(conversations.map((conversation) => conversation.user_id))];
  if (userIds.length === 0) {
    return [] as SupportConversationThread[];
  }

  const { data: profilesData, error: profilesError } = await supabase
    .from('profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', userIds);

  if (profilesError) {
    throw profilesError;
  }

  const profiles = (profilesData || []) as SupportProfileSummary[];
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));

  return conversations.map((conversation) => ({
    ...conversation,
    profile: profilesById.get(conversation.user_id) || null,
  }));
}

export async function resolveSupportMessageAttachment(message: SupportMessageLike) {
  const bucket = getAttachmentBucket(message);
  const path = getAttachmentPath(message);

  if (!bucket || bucket !== SUPPORT_ATTACHMENT_BUCKET || !path) {
    return message;
  }

  if (typeof message.media_url === 'string' && (/^https?:\/\//i.test(message.media_url) || message.media_url.startsWith('blob:'))) {
    return message;
  }

  try {
    const signedUrl = await createSupportAttachmentSignedUrl(path);
    return {
      ...message,
      media_url: signedUrl,
      metadata: {
        ...getMessageMetadata(message),
        attachment_bucket: bucket,
        attachment_path: path,
      },
    } as SupportMessage;
  } catch {
    return message;
  }
}

export async function resolveSupportMessageAttachments(messages: SupportMessageLike[]) {
  return Promise.all(messages.map(resolveSupportMessageAttachment));
}

export function hasSupportMessageAttachment(message: Pick<SupportMessageLike, 'media_url' | 'metadata'>) {
  return Boolean(getAttachmentPath(message));
}

export function getSupportMessageAttachmentName(message: Pick<SupportMessageLike, 'metadata'>) {
  const metadata = getMessageMetadata(message);
  return typeof metadata.attachment_name === 'string' ? metadata.attachment_name : null;
}

export function getSupportMessageAttachmentSize(message: Pick<SupportMessageLike, 'metadata'>) {
  const metadata = getMessageMetadata(message);
  return typeof metadata.attachment_size === 'number' ? metadata.attachment_size : null;
}

export function isSupportMessageImageAttachment(message: Pick<SupportMessageLike, 'media_type'>) {
  return Boolean(message.media_type?.startsWith('image/'));
}

export async function fetchSupportMessages(conversationId: string) {
  const { data, error } = await supabase
    .from('support_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  return resolveSupportMessageAttachments((data || []) as SupportMessage[]);
}

export async function sendSupportMessage(body: string, attachment?: SupportAttachmentUpload | null) {
  const session = await requireSessionUser();

  const response = await fetch(`${SUPPORT_FUNCTIONS_BASE_URL}/send-whatsapp-support-message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: getSupabaseKey(),
    },
    body: JSON.stringify({
      body,
      attachment: attachment
        ? {
            path: attachment.path,
            fileName: attachment.fileName,
            mimeType: attachment.mimeType,
            size: attachment.sizeBytes,
          }
        : null,
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const errorMessage = data?.error || data?.message || 'Failed to send support message.';
    throw new Error(errorMessage);
  }

  const result = data as SendSupportMessageResult;
  return {
    ...result,
    message: await resolveSupportMessageAttachment(result.message),
  } satisfies SendSupportMessageResult;
}

export async function sendAdminSupportReply(conversationId: string, body: string) {
  const session = await requireSessionUser();
  const insertPayload: TablesInsert<'support_messages'> = {
    conversation_id: conversationId,
    sender_user_id: session.user.id,
    direction: 'outbound_support',
    sender_type: 'support',
    body: body.trim(),
    delivery_status: 'delivered',
    metadata: {
      source: 'admin_panel',
    },
  };

  const { data, error } = await (supabase.from('support_messages') as any)
    .insert(insertPayload)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return resolveSupportMessageAttachment(data as SupportMessage);
}

export function upsertSupportMessage(messages: SupportMessageLike[], nextMessage: SupportMessageLike) {
  const existingIndex = messages.findIndex((message) => message.id === nextMessage.id);
  if (existingIndex === -1) {
    return sortSupportMessages([...messages, nextMessage]);
  }

  const nextMessages = [...messages];
  nextMessages[existingIndex] = { ...nextMessages[existingIndex], ...nextMessage };
  return sortSupportMessages(nextMessages);
}

export function sortSupportMessages(messages: SupportMessageLike[]) {
  return [...messages].sort((left, right) => {
    const timeDelta = new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
    if (timeDelta !== 0) {
      return timeDelta;
    }

    return left.id.localeCompare(right.id);
  });
}

export function createOptimisticSupportMessage(
  body: string,
  conversationId?: string | null,
  options: CreateOptimisticSupportMessageOptions = {}
): SupportMessageLike {
  const timestamp = new Date().toISOString();
  const senderType = options.senderType || 'user';
  const direction = options.direction || 'outbound_user';
  const deliveryStatus = options.deliveryStatus || (senderType === 'user' ? 'pending' : 'delivered');

  return {
    id: `optimistic-${crypto.randomUUID()}`,
    conversation_id: conversationId || crypto.randomUUID(),
    sender_user_id: null,
    direction,
    sender_type: senderType,
    body,
    media_url: options.mediaUrl || null,
    media_type: options.mediaType || null,
    external_message_sid: null,
    original_replied_message_sid: null,
    delivery_status: deliveryStatus,
    error_message: null,
    metadata: options.metadata || {},
    created_at: timestamp,
    updated_at: timestamp,
    optimistic: true,
  };
}
